import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

// Resizable table chat dock (SC-2393). The width is a persisted user preference;
// the drag handle clamps to this range so the dock can't be dragged uselessly
// narrow or so wide it swallows the table.
export const DEFAULT_DOCK_WIDTH = 380;
export const DOCK_MIN_WIDTH = 320;
export const DOCK_MAX_WIDTH = 640;

/** Clamp a (possibly mid-drag) width to the allowed range; NaN → the default. */
export function clampDockWidth(width: number): number {
  if (Number.isNaN(width)) return DEFAULT_DOCK_WIDTH;
  return Math.max(DOCK_MIN_WIDTH, Math.min(DOCK_MAX_WIDTH, Math.round(width)));
}

// Resizable left sidebar (SC-3491) — the EXPANDED width only; the collapsed icon
// rail stays a fixed 56px. Persisted like the dock width, clamped so long catalog
// names get room without the sidebar swallowing the canvas.
export const DEFAULT_SIDEBAR_WIDTH = 220;
export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 420;

/** Clamp a (possibly mid-drag) sidebar width to the allowed range; NaN → the default. */
export function clampSidebarWidth(width: number): number {
  if (Number.isNaN(width)) return DEFAULT_SIDEBAR_WIDTH;
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, Math.round(width)));
}

interface AppState {
  theme: 'light' | 'dark' | 'system';
  /** Currently picked catalog id. Resolved against the catalog list by
   *  useActiveCatalog() — falls back to the most-recent catalog when null
   *  or when the stored id no longer matches any catalog in the user's org
   *  (catalog deleted from another tab, user switched orgs, etc.). */
  activeCatalogId: string | null;
  /** Per-catalog flag set while a new-chat briefing regen (`?refresh=true`)
   *  is in flight. AgentTab gates the briefing bubble on this so the user
   *  sees a "Regenerating briefing…" placeholder during the ~4s LLM window
   *  instead of the stale pre-click narrative. Set true in
   *  useCreateChatSession's onSuccess, cleared in the .finally on the
   *  briefing fetch. Ephemeral — not persisted. */
  briefingRefreshing: Record<string, boolean>;
  /** Table chat dock width (px). Persisted preference; the dock and the table's
   *  reflow margin both read it (SC-2393). Always clamped via setDockWidth. */
  dockWidth: number;
  /** True while the dock's drag handle is active. Ephemeral — lets the table's
   *  margin transition switch off during a drag so the table edge tracks the
   *  dock 1:1 instead of easing behind the cursor. */
  dockResizing: boolean;
  /** Left sidebar collapsed to the icon rail (SC-2393). Persisted preference,
   *  default COLLAPSED (SC-2716): login lands in the catalog workspace, where the
   *  compact rail is the resting state and the user expands it on demand to see
   *  the catalog name + picker. */
  sidebarCollapsed: boolean;
  /** Expanded left sidebar width (px, SC-3491). Persisted preference; always
   *  clamped via setSidebarWidth (and on rehydrate). */
  sidebarWidth: number;
  /** Table facet rail collapsed (SC-2395). Persisted preference; default OPEN so
   *  the rail is discoverable, collapsible to reclaim width on wide catalogs. */
  facetRailCollapsed: boolean;
  /** The rail's built-in filters band (Completeness + channels, SC-3669) collapsed.
   *  Persisted preference; default OPEN. */
  facetBuiltinsCollapsed: boolean;
  /** Canvas P3 (SC-2659) — the permanent `ChatPanel` collapsed to its rail. The
   *  panel is a fixed member of the workspace frame, so its visibility is a
   *  persisted collapse pref, default EXPANDED (locked decision #4: chat is
   *  collapsible, default-open). */
  chatCollapsed: boolean;
  setTheme: (t: 'light' | 'dark' | 'system') => void;
  setActiveCatalogId: (id: string | null) => void;
  setBriefingRefreshing: (catalogId: string, value: boolean) => void;
  setDockWidth: (width: number) => void;
  setDockResizing: (resizing: boolean) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setFacetRailCollapsed: (collapsed: boolean) => void;
  setFacetBuiltinsCollapsed: (collapsed: boolean) => void;
  setChatCollapsed: (collapsed: boolean) => void;
  toggleChatCollapsed: () => void;
  /** Reveal the chat: un-collapse the permanent `ChatPanel`. The "Discuss"/"Ask"
   *  seed actions call this so a seeded message can never land in a hidden panel. */
  revealChat: () => void;
}

/**
 * persist `merge` — shallow-merges the persisted partial over the current state
 * (the default behavior) AND re-clamps `dockWidth` + `sidebarWidth`. The setters
 * clamp every write, but a value persisted before the bounds changed (or a
 * corrupted store) would otherwise rehydrate out of range. Pure + exported so
 * the clamp-on-rehydrate is unit-testable without the persist/storage dance.
 */
export function mergePersisted(persisted: unknown, current: AppState): AppState {
  const p = (persisted ?? {}) as Partial<AppState>;
  // `persisted` is untyped localStorage JSON — guard `dockWidth` is actually a
  // number before clamping (a corrupted non-number would otherwise pass
  // clampDockWidth's NaN check and reach the dock as NaN).
  const dockWidth = typeof p.dockWidth === 'number' ? p.dockWidth : current.dockWidth;
  const sidebarWidth =
    typeof p.sidebarWidth === 'number' ? p.sidebarWidth : current.sidebarWidth;
  return {
    ...current,
    ...p,
    dockWidth: clampDockWidth(dockWidth),
    sidebarWidth: clampSidebarWidth(sidebarWidth),
  };
}

/**
 * `localStorage` with every access wrapped (SC-3491). persist already survives a
 * throwing storage *lookup*, but not a throwing `setItem` — a blocked or full store
 * (Safari private mode, a quota error — both DOMExceptions) would make every
 * setter throw, mid-drag included. Failing soft keeps the in-memory state working
 * on the defaults.
 */
export const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name);
    } catch (e) {
      if (e instanceof DOMException) return null;
      throw e;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch (e) {
      if (!(e instanceof DOMException)) throw e;
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch (e) {
      if (!(e instanceof DOMException)) throw e;
    }
  },
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      theme: 'system',
      activeCatalogId: null,
      briefingRefreshing: {},
      dockWidth: DEFAULT_DOCK_WIDTH,
      dockResizing: false,
      sidebarCollapsed: true,
      sidebarWidth: DEFAULT_SIDEBAR_WIDTH,
      facetRailCollapsed: false,
      facetBuiltinsCollapsed: false,
      chatCollapsed: false,
      setTheme: (theme) => set({ theme }),
      setActiveCatalogId: (activeCatalogId) => set({ activeCatalogId }),
      setBriefingRefreshing: (catalogId, value) =>
        set((state) => ({
          briefingRefreshing: { ...state.briefingRefreshing, [catalogId]: value },
        })),
      // Clamp in the setter so every writer (drag, double-click reset, keyboard
      // nudge) lands in range — the callers don't each repeat the bounds.
      setDockWidth: (width) => set({ dockWidth: clampDockWidth(width) }),
      setDockResizing: (dockResizing) => set({ dockResizing }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setSidebarWidth: (width) => set({ sidebarWidth: clampSidebarWidth(width) }),
      setFacetRailCollapsed: (facetRailCollapsed) => set({ facetRailCollapsed }),
      setFacetBuiltinsCollapsed: (facetBuiltinsCollapsed) => set({ facetBuiltinsCollapsed }),
      setChatCollapsed: (chatCollapsed) => set({ chatCollapsed }),
      toggleChatCollapsed: () => set((state) => ({ chatCollapsed: !state.chatCollapsed })),
      revealChat: () => set({ chatCollapsed: false }),
    }),
    {
      name: 'ekom-app-store',
      storage: createJSONStorage(() => safeLocalStorage),
      // Clamp `dockWidth` + `sidebarWidth` on rehydrate (the setters only clamp
      // live writes).
      merge: mergePersisted,
      // Persist the active catalog selection and the layout preferences (dock
      // width + sidebar collapsed/width + facet rail and built-ins collapsed + chat
      // collapsed).
      // `theme` follows the OS preference on each load; `briefingRefreshing` /
      // `dockResizing` are ephemeral UI state.
      partialize: (state) => ({
        activeCatalogId: state.activeCatalogId,
        dockWidth: state.dockWidth,
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
        facetRailCollapsed: state.facetRailCollapsed,
        facetBuiltinsCollapsed: state.facetBuiltinsCollapsed,
        chatCollapsed: state.chatCollapsed,
      }),
    },
  ),
);
